import { useTranslation } from 'react-i18next'
import type { ComponentProgressDetailView } from '@/types/coaching'
import { stripShortPointPrefix, WarmupTextView } from '@/components/common/WarmupTextView'
import { LearnerMirrorSection } from './parts/LearnerMirrorSection'
import { AudioResponseList } from './parts/AudioResponseList'
import { PartialDataFallback } from './parts/PartialDataFallback'

interface Props {
  component: ComponentProgressDetailView
}

export function WarmupCoachingDetail({ component }: Props) {
  const { t } = useTranslation('coaching')
  const warmup = component.warmup
  if (!warmup) return <PartialDataFallback type="warmup" />

  const audioResponse = warmup.audioResponse1Url ?? null
  const hasMirrorContent = !!(warmup.warmupText || warmup.shortPoint || audioResponse)
  const skipReason =
    component.status === 'skipped' &&
    (component.skippedReason === 'learner_choice' || component.skippedReason === 'content_missing')
      ? component.skippedReason
      : null

  return (
    <div className="space-y-3">
      {skipReason ? (
        <div
          role="note"
          data-testid="warmup-skip-notice"
          className={
            skipReason === 'content_missing'
              ? 'rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900'
              : 'rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700'
          }
        >
          <p className="font-semibold">{t(`unit.skipReason.${skipReason}`)}</p>
          <p className="mt-0.5">{t(`unit.skipReasonDetail.${skipReason}`)}</p>
        </div>
      ) : null}
      <LearnerMirrorSection title={t('section.learnerView')}>
        {hasMirrorContent ? (
          <div className="space-y-2">
            {warmup.warmupText && (
              <div className="rounded border border-gray-200 bg-white p-2">
                <p className="text-xs font-bold text-gray-700">{t('warmupDetail.thinkingOpener')}</p>
                <WarmupTextView
                  text={warmup.warmupText}
                  className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-800"
                />
              </div>
            )}
            {warmup.shortPoint && (
              <div className="rounded border border-gray-200 bg-white p-2">
                <p className="text-xs font-bold text-gray-700">{t('warmupDetail.point')}</p>
                <WarmupTextView
                  text={stripShortPointPrefix(warmup.shortPoint)}
                  className="mt-1 text-sm leading-6 text-gray-800"
                />
              </div>
            )}
            <div className="rounded border border-gray-200 bg-white p-2">
              <AudioResponseList response1={audioResponse} response2={null} />
            </div>
          </div>
        ) : (
          <p className="text-xs italic text-gray-500">{t('placeholder.comingSoon')}</p>
        )}
      </LearnerMirrorSection>
    </div>
  )
}
